import { Link } from 'react-router-dom';
import { ShieldCheck, AlertTriangle, ArrowLeft } from 'lucide-react';

const LAST_UPDATED = '8 June 2026';

// Faithful representation of the AVZ Wholesale Terms & Conditions document.
const SECTIONS = [
  {
    title: 'Overview',
    blocks: [
      { p: 'AVZ Wholesale operates as a community-driven group buying service, consolidating customer orders to access wholesale pricing from trusted suppliers.' },
      { p: 'By placing an order with AVZ Wholesale, you agree to the Terms & Conditions outlined below.' },
    ],
  },
  {
    title: 'How Ordering Works',
    blocks: [
      { list: [
        'Orders are placed within specified order windows.',
        'All orders are consolidated and submitted in bulk to suppliers.',
        'AVZ Wholesale does not hold stock — all products are ordered specifically per cycle.',
      ] },
      { warn: 'This means:' },
      { list: [
        'Orders cannot be changed once placed, and cannot be cancelled once the order window closes.',
        'Delivery timelines depend on supplier processing and logistics.',
      ] },
    ],
  },
  {
    title: 'Pricing & Fees',
    blocks: [
      { p: 'To keep pricing transparent and sustainable:' },
      { list: [
        'Standard Admin Fee: 8% added to order total.',
        'Lowerland Orders: 10% admin fee.',
        'Transport Fee: R15 per order (distribution to collection points).',
        'Supplier Delivery Fees: charged proportionally based on the total shipment (some suppliers only).',
      ] },
      { p: 'All prices are subject to supplier price changes prior to order confirmation.' },
    ],
  },
  {
    title: 'Payment Terms',
    blocks: [
      { list: [
        'Full payment is required before orders are submitted.',
        'Orders will only be processed once proof of payment is received.',
      ] },
      { sub: 'Late payments may result in:' },
      { list: [
        'Exclusion from that order cycle.',
        'Orders cancelled if not paid before the order window closes.',
      ] },
    ],
  },
  {
    title: 'Order Fulfilment & Timelines',
    blocks: [
      { list: [
        'Estimated timelines will be communicated per order cycle.',
        'AVZ Wholesale is not liable for supplier or courier delays, although every effort will be made to keep customers informed.',
        'Orders are unpacked, sorted, and prepared after delivery before collection is arranged.',
      ] },
    ],
  },
  {
    title: 'Collections',
    blocks: [
      { list: [
        'Orders must be collected from designated collection points.',
        'Customers will be notified once orders are ready.',
        "Perishable items are collected strictly at the customer's own risk after the advised collection window.",
      ] },
      { sub: 'Collection Guidelines:' },
      { list: [
        'Please collect within 48 hours where applicable (especially business locations).',
        'Collection fees apply.',
        'No products will be kept refrigerated or frozen.',
      ] },
      { sub: 'Failure to collect timeously may result in:' },
      { list: [
        'Items returned to hub (ADW) and will need to be collected there.',
        'Risk of product spoilage (where applicable).',
      ] },
    ],
  },
  {
    title: 'Product Availability & Substitutions',
    blocks: [
      { p: 'All products are subject to supplier availability.' },
      { sub: 'If an item is out of stock or short supplied, we will:' },
      { list: [
        'Issue a refund, OR',
        'Offer an alternative where appropriate (with communication where possible).',
      ] },
    ],
  },
  {
    title: 'Refunds & Returns',
    blocks: [
      { p: 'Due to the nature of bulk ordering:' },
      { sub: 'Returns are not accepted unless:' },
      { list: [
        'The item is damaged, defective, or incorrect and the supplier is willing to accept the return.',
        'Claims must be reported within 24 hours of collection, with supporting photos.',
      ] },
      { sub: 'Refunds will be processed:' },
      { list: [
        'After verification.',
        'In the event that the supplier is willing to grant a refund.',
      ] },
    ],
  },
  {
    title: 'Quality & Product Responsibility',
    blocks: [
      { p: 'AVZ Wholesale sources from trusted suppliers but does not manufacture products and is in no way affiliated with the suppliers.' },
      { sub: 'We are not liable for:' },
      { list: ['Manufacturer defects.', 'Allergic reactions or product misuse.'] },
      { p: 'Customers are responsible for checking product suitability and expiry dates.' },
    ],
  },
  {
    title: 'Collection Point Courtesy',
    blocks: [
      { list: [
        'Collection points are hosted by individuals and businesses — please respect their time and premises.',
        'Pay careful attention to the instructions for your chosen collection point and adhere to agreed times.',
      ] },
    ],
  },
  {
    title: 'Limitation of Liability',
    blocks: [
      { p: 'AVZ Wholesale operates as a facilitator between customers and suppliers.' },
      { sub: 'We are not liable for:' },
      { list: [
        'Supplier delays or shortages.',
        'Courier issues or damages.',
        'Loss or damage after collection.',
        'Indirect or consequential losses.',
      ] },
    ],
  },
  {
    title: 'Communication',
    blocks: [
      { p: 'All key updates (orders, payments, delivery, collections) will be communicated via WhatsApp and email.' },
      { p: 'Customers are responsible for staying informed.' },
    ],
  },
  {
    title: 'Updates to Terms',
    blocks: [
      { p: 'AVZ Wholesale reserves the right to update these Terms & Conditions at any time. Continued use of the service constitutes acceptance of any changes.' },
    ],
  },
  {
    title: 'Acceptance of Terms',
    blocks: [
      { sub: 'By placing an order, you confirm that you:' },
      { list: [
        'Understand how the group buying model works.',
        'Accept all pricing, timelines, and processes.',
        'Agree to these Terms & Conditions.',
      ] },
    ],
  },
  {
    title: 'POPIA & Privacy Policy',
    blocks: [
      { p: 'AVZ Wholesale is committed to protecting your personal information in accordance with the Protection of Personal Information Act (POPIA).' },
      { sub: 'By placing an order, you agree that we may collect and process your personal information, including:' },
      { list: ['Name and surname.', 'Contact details (phone number, email).', 'Delivery / collection information.'] },
      { sub: 'Your information will be used solely for:' },
      { list: ['Processing and fulfilling your orders.', 'Communicating order updates and collections.', 'Managing payments and records.'] },
      { sub: 'Your information will not be sold or shared with third parties, except where necessary to:' },
      { list: ['Complete supplier orders.', 'Arrange courier deliveries.'] },
      { p: 'All reasonable steps are taken to protect your data from loss, misuse, or unauthorised access.' },
      { sub: 'You may request:' },
      { list: ['Access to your personal information.', 'Correction or deletion of your data.'] },
      { p: 'Requests can be made directly to AVZ Wholesale via your usual communication channel.' },
    ],
  },
  {
    title: 'Perishable Goods Disclaimer',
    blocks: [
      { p: 'Due to the nature of certain products, AVZ Wholesale handles perishable and temperature-sensitive goods.' },
      { sub: 'By placing an order, you acknowledge and accept that:' },
      { list: ['Some items require prompt collection and proper storage after pickup.'] },
      { sub: 'AVZ Wholesale is not responsible for:' },
      { list: ['Spoilage after collection.', 'Temperature damage once products have been distributed to collection points.'] },
      { sub: 'Customers are responsible for:' },
      { list: ['Collecting within the recommended timeframes (ideally within 24–48 hours).', 'Ensuring correct storage (e.g. refrigeration / freezing where required).'] },
      { warn: 'Important: If perishable goods are not collected in time, quality cannot be guaranteed. Refunds will not be issued for spoilage due to late collection or improper storage.' },
    ],
  },
];

const Block = ({ block }) => {
  if (block.p) return <p className="font-body text-sm sm:text-base text-foreground/70 leading-relaxed mb-3">{block.p}</p>;
  if (block.sub) return <p className="font-body text-sm font-semibold text-foreground/90 mt-4 mb-2">{block.sub}</p>;
  if (block.warn) {
    return (
      <div className="flex items-start gap-2.5 my-3 rounded-xl bg-gold/5 border border-gold/25 p-3.5">
        <AlertTriangle className="h-4 w-4 text-gold flex-shrink-0 mt-0.5" strokeWidth={2} />
        <p className="font-body text-sm text-foreground/80 leading-relaxed">{block.warn}</p>
      </div>
    );
  }
  if (block.list) {
    return (
      <ul className="space-y-1.5 mb-3 ml-1">
        {block.list.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5 font-body text-sm sm:text-base text-foreground/70 leading-relaxed">
            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-teal flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  return null;
};

export default function TermsPage() {
  return (
    <div data-testid="terms-page" className="bg-background min-h-screen">
      {/* Header */}
      <section className="border-b border-border bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <Link to="/" className="inline-flex items-center gap-1.5 font-body text-xs text-foreground/50 hover:text-teal transition-colors mb-6">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 rounded-xl bg-teal/10">
              <ShieldCheck className="h-6 w-6 text-teal" strokeWidth={1.75} />
            </div>
            <p className="font-body text-xs sm:text-sm uppercase tracking-[0.3em] text-teal font-bold">
              AVZ Wholesale
            </p>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light text-foreground tracking-tight mb-3">
            Terms &amp; Conditions
          </h1>
          <p className="font-body text-sm text-foreground/55">Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="space-y-8 sm:space-y-10">
          {SECTIONS.map((section, idx) => (
            <div key={idx} data-testid={`terms-section-${idx}`} className="scroll-mt-24">
              <h2 className="font-heading text-xl sm:text-2xl font-medium text-foreground mb-3 flex items-baseline gap-3">
                <span className="font-body text-sm font-bold text-gold">{String(idx + 1).padStart(2, '0')}</span>
                {section.title}
              </h2>
              <div className="pl-0 sm:pl-9">
                {section.blocks.map((b, i) => <Block key={i} block={b} />)}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-2xl bg-card border border-border p-6 sm:p-8 text-center">
          <p className="font-body text-sm text-foreground/60 leading-relaxed">
            Questions about these terms? Reach out via your usual WhatsApp or email channel and we&apos;ll be happy to help.
          </p>
          <Link to="/shop">
            <span className="inline-block mt-4 font-body text-sm font-semibold text-teal hover:text-teal/80 transition-colors">
              Continue shopping →
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
}
